import React, { useState, useEffect, useCallback } from "react";
import API_BASE_URL from "../config";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import {
    Eye, CheckCircle, XCircle, Clock, RefreshCw,
    Printer, X, Search, Filter, Download, RotateCcw,
    ClipboardList, Users, Building2, CalendarDays,
} from "lucide-react";

// ── Field accessors ──────────────────────────────────────────────────────────
const f = {
    id:           r => r.id              || 0,
    controlNo:    r => r.control_No      || r.Control_No    || r.controlNo    || "",
    department:   r => r.department      || r.Department    || "",
    date:         r => r.date            || r.Date          || "",
    preparedBy:   r => r.preparedBy      || r.PreparedBy    || "",
    itemName:     r => r.item_Name       || r.Item_Name     || r.itemName     || "",
    quantity:     r => r.quantity        || r.Quantity      || "",
    unit:         r => r.unit            || r.Unit          || "",
    description:  r => r.description     || r.Description   || "",
    remarks:      r => r.remarks         || r.Remarks       || "",
    deptHead:     r => r.department_Head || r.Department_Head || r.departmentHead || "",
    approvedBy:   r => r.approved_By     || r.Approved_By   || r.approvedBy   || "",
    receivedBy:   r => r.item_ReceivedBy || r.Item_ReceivedBy || r.itemReceivedBy || "",
    created:      r => r.created         || r.Created       || "",
    isApproved:   r => r.isApproved      ?? r.IsApproved    ?? null,
    isDisapproved:r => r.isDisapproved   ?? r.IsDisapproved ?? null,
};

const getStatus = (row) => {
    if (f.isApproved(row) === true)    return "approved";
    if (f.isDisapproved(row) === true) return "disapproved";
    return "pending";
};

const STATUS_CONFIG = {
    approved:    { label: "Approved",    color: "text-green-700",  bg: "bg-green-50",  border: "border-green-300",  icon: CheckCircle,  iconColor: "text-green-600"  },
    disapproved: { label: "Disapproved", color: "text-red-700",    bg: "bg-red-50",    border: "border-red-300",    icon: XCircle,      iconColor: "text-red-600"    },
    pending:     { label: "Pending",     color: "text-amber-700",  bg: "bg-amber-50",  border: "border-amber-300",  icon: Clock,        iconColor: "text-amber-500"  },
};

const PAGE_SIZE = 10;

// ── Print style ──────────────────────────────────────────────────────────────
if (!document.getElementById("disposal-print-style")) {
    const tag = document.createElement("style");
    tag.id = "disposal-print-style";
    tag.innerHTML = `
        @media print {
            body * { visibility: hidden !important; }
            #disposal-print-area, #disposal-print-area * { visibility: visible !important; }
            #disposal-print-area {
                position: fixed !important; inset: 0 !important;
                background: #fff !important; padding: 18mm 15mm !important;
                box-sizing: border-box !important; z-index: 99999 !important;
                font-family: Arial, sans-serif !important;
            }
            @page { size: A4 portrait; margin: 0; }
        }
    `;
    document.head.appendChild(tag);
}

// ── StatusBadge ──────────────────────────────────────────────────────────────
const StatusBadge = ({ row }) => {
    const cfg = STATUS_CONFIG[getStatus(row)];
    const Icon = cfg.icon;
    return (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border ${cfg.bg} ${cfg.color} ${cfg.border}`}>
            <Icon size={12} className={cfg.iconColor} />
            {cfg.label}
        </span>
    );
};

// ── Clean print document ─────────────────────────────────────────────────────
// ── Clean print document ─────────────────────────────────────────────────────
const DisposalDocument = ({ row }) => {
    const quantities   = (f.quantity(row)    || "").split(",").map(s => s.trim());
    const units        = (f.unit(row)        || "").split(",").map(s => s.trim());
    const names        = (f.itemName(row)    || "").split(",").map(s => s.trim());
    const descriptions = (f.description(row) || "").split(",").map(s => s.trim());
    const remarks      = (f.remarks(row)     || "").split(",").map(s => s.trim());
    const rowCount     = Math.max(names.length, 6);
    const docDate      = f.date(row)
        ? new Date(f.date(row)).toLocaleDateString("en-US", { month: "numeric", day: "numeric", year: "numeric" })
        : "—";

    const b  = { border: "1px solid #000", padding: "4px 7px", fontSize: "10.5px", verticalAlign: "middle" };
    const bc = { ...b, textAlign: "center" };

    return (
        <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #000", fontFamily: "Arial, sans-serif" }}>
            <tbody>
                <tr>
                    <td colSpan={2} style={{ ...b, width: "55%", padding: "8px 10px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <div style={{ width: "42px", height: "42px", borderRadius: "50%", border: "2px solid #1e40af", display: "flex", alignItems: "center", justifyContent: "center", color: "#1e40af", fontSize: "10px", fontWeight: "900", flexShrink: 0 }}>MS</div>
                            <div>
                                <div style={{ fontWeight: "900", fontSize: "12px" }}>MEGA SOFT Hygienic Products, Inc.</div>
                                <div style={{ fontSize: "8.5px", color: "#444" }}>34 P. Jacinto, Pleasant View Drive, Brgy. 165 Bagbaguin Caloocan City</div>
                            </div>
                        </div>
                    </td>
                    <td colSpan={3} style={{ ...b, textAlign: "right", verticalAlign: "top", padding: "8px 10px" }}>
                        <div style={{ fontSize: "8.5px", color: "#555", marginBottom: "2px" }}>CONTROL NO.</div>
                        <div style={{ fontWeight: "900", fontSize: "13px", color: "#1e40af" }}>{f.controlNo(row) || "—"}</div>
                    </td>
                </tr>
                <tr>
                    <td style={{ ...b, fontSize: "8.5px", width: "18%", verticalAlign: "top" }}>
                        <div style={{ color: "#555" }}>DOCUMENT CODE</div>
                        <div style={{ fontWeight: "800", fontSize: "9px", marginTop: "2px" }}>SPR-F-04-DIS-004</div>
                    </td>
                    <td colSpan={4} style={{ ...bc }}>
                        <div style={{ fontSize: "8.5px", color: "#555", marginBottom: "1px" }}>FORMS AND CHECKLIST</div>
                        <div style={{ fontWeight: "900", fontSize: "16px", letterSpacing: "1.5px" }}>ITEM DISPOSAL FORM</div>
                    </td>
                </tr>
                <tr>
                    <td style={{ ...b, fontSize: "8.5px", verticalAlign: "top" }}>
                        <div style={{ color: "#555" }}>DOCUMENT OWNER</div>
                        <div style={{ fontWeight: "800", fontSize: "9px", marginTop: "1px" }}>SPAREPARTS DEPARTMENT</div>
                    </td>
                    <td style={{ ...bc, fontSize: "8.5px", width: "9%" }}>
                        <div style={{ color: "#555" }}>ISSUE NO</div>
                        <div style={{ fontWeight: "800", marginTop: "1px" }}>1</div>
                    </td>
                    <td style={{ ...bc, fontSize: "8.5px", width: "11%" }}>
                        <div style={{ color: "#555" }}>REVISION NO.</div>
                        <div style={{ fontWeight: "800", marginTop: "1px" }}>0</div>
                    </td>
                    <td colSpan={2} style={{ ...bc, fontSize: "8.5px" }}>
                        <div style={{ color: "#555" }}>REVISION DATE | EFFECTIVITY DATE | NO. OF PAGES | PAGE</div>
                        <div style={{ fontWeight: "800", marginTop: "1px" }}>NONE | February, 2026 | 1 | 1</div>
                    </td>
                </tr>
                <tr>
                    <td colSpan={5} style={{ ...b, fontSize: "10.5px", padding: "6px 8px" }}>
                        <span style={{ fontWeight: "800", marginRight: "10px" }}>DEPARTMENT:</span>
                        <span style={{ borderBottom: "1px solid #000", paddingBottom: "1px", display: "inline-block", minWidth: "220px" }}>{f.department(row) || ""}</span>
                    </td>
                </tr>
                <tr>
                    {[{ label: "QUANTITY", w: "10%" }, { label: "UNIT", w: "10%" }, { label: "ITEM NAME", w: "32%" }, { label: "DESCRIPTION", w: "26%" }, { label: "REMARKS", w: "22%" }].map(({ label, w }) => (
                        <td key={label} style={{ ...bc, fontWeight: "800", fontSize: "10.5px", width: w, backgroundColor: "#f0f0f0" }}>{label}</td>
                    ))}
                </tr>
                {Array.from({ length: rowCount }, (_, i) => (
                    <tr key={i}>
                        <td style={{ ...bc, height: "22px", fontSize: "10.5px" }}>{quantities[i] || ""}</td>
                        <td style={{ ...bc, fontSize: "10.5px" }}>{units[i] || ""}</td>
                        <td style={{ ...b, fontSize: "10.5px", fontWeight: names[i] ? "600" : "400" }}>{names[i] || ""}</td>
                        <td style={{ ...bc, fontSize: "10.5px" }}>{descriptions[i] || ""}</td>
                        <td style={{ ...bc, fontSize: "10.5px" }}>{remarks[i] || ""}</td>
                    </tr>
                ))}
                <tr>
                    <td colSpan={5} style={{ ...bc, fontWeight: "700", fontSize: "10.5px", padding: "5px", backgroundColor: "#f9f9f9" }}>**NOTHING FOLLOWS**</td>
                </tr>

                {/* ── SIGNATORIES ROW — Prepared By + Dept Head + Approved By in one row ── */}
        {/* ── SIGNATORIES ROW ── */}
<tr>
    {/* PREPARED BY */}
    <td style={{ ...b, padding: "14px 10px", fontSize: "10px", verticalAlign: "top", width: "34%" }}>
        <div style={{ fontWeight: "800", marginBottom: "28px" }}>PREPARED BY:</div>
        <div style={{ textAlign: "center" }}>
            <div style={{ fontWeight: "900", fontSize: "11.5px", textTransform: "uppercase", textDecoration: "underline" }}>
                {f.preparedBy(row) || "\u00A0".repeat(20)}
            </div>
            <div style={{ fontSize: "8.5px", color: "#555", marginTop: "3px" }}>Spare Parts Personnel</div>
            <div style={{ fontSize: "8.5px", color: "#555", marginTop: "4px" }}>
                Date:&nbsp;
                <span style={{ borderBottom: "1px solid #000", paddingBottom: "1px", minWidth: "80px", display: "inline-block", fontWeight: "700" }}>
                    {docDate}
                </span>
            </div>
        </div>
    </td>

    {/* DEPARTMENT HEAD */}
    <td colSpan={2} style={{ ...b, padding: "14px 10px", fontSize: "10px", verticalAlign: "top", width: "33%" }}>
        <div style={{ fontWeight: "800", marginBottom: "28px" }}>Department Head:</div>
        <div style={{ textAlign: "center" }}>
            <div style={{ fontWeight: "900", fontSize: "11.5px", textDecoration: "underline", textTransform: "uppercase" }}>
                {f.deptHead(row) || "\u00A0".repeat(20)}
            </div>
            <div style={{ fontSize: "8.5px", color: "#555", marginTop: "3px" }}>Signature over Printed Name</div>
        </div>
    </td>

    {/* APPROVED BY */}
    <td colSpan={2} style={{ ...b, padding: "14px 10px", fontSize: "10px", verticalAlign: "top", width: "33%" }}>
        <div style={{ fontWeight: "800", marginBottom: "28px" }}>Approved by:</div>
        <div style={{ textAlign: "center" }}>
            <div style={{ fontWeight: "900", fontSize: "11.5px", textDecoration: "underline", textTransform: "uppercase" }}>
                {f.approvedBy(row) || "\u00A0".repeat(20)}
            </div>
                        <div style={{ fontSize: "8.5px", color: "#555", marginTop: "3px" }}>Signature over Printed Name</div>

        </div>
    </td>
</tr>

                {/* ITEM RECEIVED BY */}
                <tr>
                    <td colSpan={5} style={{ ...b, padding: "12px 10px", fontSize: "10px" }}>
                        <div style={{ fontWeight: "800", marginBottom: "16px" }}>Item Received by:</div>
                        <div style={{ paddingLeft: "10px" }}>
                            <div style={{ fontWeight: "900", fontSize: "11.5px", textDecoration: "underline", textTransform: "uppercase" }}>
                                {f.receivedBy(row) || "\u00A0".repeat(24)}
                            </div>
                            <div style={{ fontSize: "8.5px", color: "#555", marginTop: "3px" }}>Scrap Officer In-Charge</div>
                        </div>
                    </td>
                </tr>

                <tr>
                    <td colSpan={5} style={{ ...bc, fontSize: "8.5px", color: "#555", fontStyle: "italic", padding: "5px" }}>
                        This document is a property of Mega Soft. Unauthorized copying, reproduction, distribution, or alteration is strictly prohibited.
                    </td>
                </tr>
            </tbody>
        </table>
    );
};

// ── MAIN COMPONENT ───────────────────────────────────────────────────────────
const DisposalApproval = () => {
    const [currentUser, setCurrentUser] = useState(null);
    useEffect(() => {
        const stored = localStorage.getItem("user");
        if (stored) { try { setCurrentUser(JSON.parse(stored)); } catch {} }
    }, []);

    const userName = currentUser?.fullName || currentUser?.name || currentUser?.Name || currentUser?.username || "Unknown User";

    const [records, setRecords]             = useState([]);
    const [loading, setLoading]             = useState(false);
    const [actionLoading, setActionLoading] = useState(null);
    const [search, setSearch]               = useState("");
    const [filterStatus, setFilterStatus]   = useState("");
    const [filterDept, setFilterDept]       = useState("");
    const [filterDate, setFilterDate]       = useState("");
    const [currentPage, setCurrentPage]     = useState(1);
    const [selectedRow, setSelectedRow]     = useState(null);
    const [departments, setDepartments]     = useState([]);

    const fetchRecords = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}api/DisposalForm`);
            if (res.ok) setRecords(await res.json());
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchRecords(); }, [fetchRecords]);

    useEffect(() => {
        fetch(`${API_BASE_URL}api/Department`)
            .then(r => r.ok ? r.json() : [])
            .then(d => setDepartments(d.filter(x => x.isActive === 1)))
            .catch(() => {});
    }, []);

    const handleAction = async (row, action) => {
        const isApprove = action === "approve";
        const label     = isApprove ? "Approve" : "Disapprove";
        const cfg       = isApprove ? STATUS_CONFIG.approved : STATUS_CONFIG.disapproved;

        const confirm = await Swal.fire({
            icon: isApprove ? "question" : "warning",
            title: `${label} this form?`,
            html: `<div style="text-align:left;font-size:14px;line-height:1.7">
                <p><b>Control No:</b> ${f.controlNo(row)}</p>
                <p><b>Department:</b> ${f.department(row)}</p>
                <p><b>Prepared By:</b> ${f.preparedBy(row)}</p>
            </div>`,
            showCancelButton: true,
            confirmButtonColor: isApprove ? "#16a34a" : "#dc2626",
            cancelButtonColor: "#6b7280",
            confirmButtonText: `Yes, ${label}`,
            cancelButtonText: "Cancel",
        });
        if (!confirm.isConfirmed) return;

        setActionLoading(f.id(row));
        try {
            const getRes = await fetch(`${API_BASE_URL}api/DisposalForm/${f.id(row)}`);
            if (!getRes.ok) throw new Error("Failed to fetch record.");
            const current = await getRes.json();
            const putRes  = await fetch(`${API_BASE_URL}api/DisposalForm/${f.id(row)}`, {
                method: "PUT", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ...current, isApproved: isApprove, isDisapproved: !isApprove }),
            });
            if (!putRes.ok) throw new Error("Update failed.");

            try {
                await fetch(`${API_BASE_URL}api/Logs`, {
                    method: "POST", headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        action: label.toUpperCase(), module: "Disposal Approval",
                        description: `${label}d Disposal Form #${f.controlNo(row)}`,
                        userId: currentUser?.id || currentUser?.userId,
                        username: currentUser?.username, fullName: userName,
                    }),
                });
            } catch {}

            await Swal.fire({ icon: "success", title: `${label}d!`, text: `${f.controlNo(row)} has been ${label.toLowerCase()}d.`, timer: 2000, showConfirmButton: false });

            const patch = { isApproved: isApprove, isDisapproved: !isApprove };
            setRecords(prev => prev.map(r => f.id(r) === f.id(row) ? { ...r, ...patch } : r));
            if (selectedRow && f.id(selectedRow) === f.id(row)) setSelectedRow(s => ({ ...s, ...patch }));
        } catch (err) {
            Swal.fire({ icon: "error", title: "Action Failed", text: err.message, confirmButtonColor: "#dc2626" });
        } finally { setActionLoading(null); }
    };

    const handleResetStatus = async (row) => {
        const confirm = await Swal.fire({
            icon: "warning", title: "Reset to Pending?",
            text: `This will reset ${f.controlNo(row)} back to Pending.`,
            showCancelButton: true, confirmButtonColor: "#d97706", cancelButtonColor: "#6b7280",
            confirmButtonText: "Yes, Reset",
        });
        if (!confirm.isConfirmed) return;

        setActionLoading(f.id(row));
        try {
            const getRes = await fetch(`${API_BASE_URL}api/DisposalForm/${f.id(row)}`);
            if (!getRes.ok) throw new Error("Failed to fetch.");
            const current = await getRes.json();
            const putRes  = await fetch(`${API_BASE_URL}api/DisposalForm/${f.id(row)}`, {
                method: "PUT", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ...current, isApproved: null, isDisapproved: null }),
            });
            if (!putRes.ok) throw new Error("Reset failed.");
            const patch = { isApproved: null, isDisapproved: null };
            setRecords(prev => prev.map(r => f.id(r) === f.id(row) ? { ...r, ...patch } : r));
            if (selectedRow && f.id(selectedRow) === f.id(row)) setSelectedRow(s => ({ ...s, ...patch }));
            Swal.fire({ icon: "success", title: "Reset to Pending", timer: 1800, showConfirmButton: false });
        } catch (err) {
            Swal.fire({ icon: "error", title: "Reset Failed", text: err.message, confirmButtonColor: "#dc2626" });
        } finally { setActionLoading(null); }
    };

    const filtered = records.filter(row => {
        const q = search.toLowerCase();
        const status = getStatus(row);
        return (
            (!q || f.controlNo(row).toLowerCase().includes(q) || f.department(row).toLowerCase().includes(q) || f.preparedBy(row).toLowerCase().includes(q) || f.itemName(row).toLowerCase().includes(q)) &&
            (!filterStatus || status === filterStatus) &&
            (!filterDept   || f.department(row) === filterDept) &&
            (!filterDate   || f.date(row).startsWith(filterDate))
        );
    });

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const paginated  = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    const counts = {
        total:       records.length,
        pending:     records.filter(r => getStatus(r) === "pending").length,
        approved:    records.filter(r => getStatus(r) === "approved").length,
        disapproved: records.filter(r => getStatus(r) === "disapproved").length,
    };

    const exportToExcel = () => {
        const data = filtered.map((row, i) => ({
            "#": i + 1, "Control No": f.controlNo(row), "Department": f.department(row),
            "Date": f.date(row) ? new Date(f.date(row)).toLocaleDateString("en-US") : "",
            "Prepared By": f.preparedBy(row), "Item Name": f.itemName(row),
            "Quantity": f.quantity(row), "Unit": f.unit(row),
            "Description": f.description(row), "Remarks": f.remarks(row),
            "Dept Head": f.deptHead(row), "Approved By": f.approvedBy(row),
            "Received By": f.receivedBy(row), "Status": STATUS_CONFIG[getStatus(row)].label,
        }));
        const ws = XLSX.utils.json_to_sheet(data);
        ws["!cols"] = Object.keys(data[0] || {}).map(k => ({ wch: Math.max(k.length, ...data.map(r => String(r[k] || "").length)) + 2 }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Disposal Approvals");
        XLSX.writeFile(wb, `DisposalApprovals_${new Date().toISOString().split("T")[0]}.xlsx`);
    };

    const paginBtn = (disabled) => `px-3 py-1.5 rounded-md text-xs font-semibold border transition-all ${disabled ? "border-gray-200 bg-gray-50 text-gray-300 cursor-not-allowed" : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50 cursor-pointer"}`;

    return (
        <div className="min-h-screen bg-slate-100 p-8 font-sans">
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden w-full">

                {/* ── PAGE HEADER ── */}
                <div className="bg-gradient-to-r from-blue-800 to-blue-600 px-8 py-6 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="bg-white/20 p-2.5 rounded-xl">
                            <ClipboardList className="text-white" size={24} />
                        </div>
                        <div>
                            <h1 className="text-white font-extrabold text-xl leading-tight">Disposal Form Approval</h1>
                            <p className="text-blue-200 text-xs mt-0.5">Mega Soft Hygienic Products, Inc. · Spareparts Department</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 bg-white/15 border border-white/20 rounded-lg px-3 py-1.5">
                        <Users size={14} className="text-white/70" />
                        <span className="text-white text-sm font-semibold">{userName}</span>
                    </div>
                </div>

                <div className="p-8">

                    {/* ── STAT CARDS ── */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                        {[
                            { label: "Total",       count: counts.total,       color: "text-blue-700",  bg: "bg-blue-50",   border: "border-blue-200",  icon: ClipboardList, val: "" },
                            { label: "Pending",     count: counts.pending,     color: "text-amber-700", bg: "bg-amber-50",  border: "border-amber-200", icon: Clock,         val: "pending" },
                            { label: "Approved",    count: counts.approved,    color: "text-green-700", bg: "bg-green-50",  border: "border-green-200", icon: CheckCircle,   val: "approved" },
                            { label: "Disapproved", count: counts.disapproved, color: "text-red-700",   bg: "bg-red-50",    border: "border-red-200",   icon: XCircle,       val: "disapproved" },
                        ].map(c => {
                            const Icon    = c.icon;
                            const active  = filterStatus === c.val && (c.val !== "" || filterStatus === "");
                            return (
                                <div key={c.label}
                                    onClick={() => { setFilterStatus(filterStatus === c.val ? (c.val === "" ? "" : "") : c.val); setCurrentPage(1); }}
                                    className={`${c.bg} border ${c.border} rounded-xl p-4 flex items-center gap-3 cursor-pointer hover:shadow-md transition-all select-none ${active && c.val !== "" ? "ring-2 ring-offset-1 ring-blue-400" : ""}`}>
                                    <div className={`p-2 rounded-lg bg-white shadow-sm`}>
                                        <Icon size={18} className={c.color} />
                                    </div>
                                    <div>
                                        <div className={`text-2xl font-extrabold leading-none ${c.color}`}>{c.count}</div>
                                        <div className="text-xs text-slate-500 font-semibold uppercase tracking-wide mt-0.5">{c.label}</div>
                                    </div>
                                    {active && c.val !== "" && (
                                        <span className="ml-auto text-[9px] font-bold bg-blue-600 text-white rounded px-1.5 py-0.5">ACTIVE</span>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* ── TOOLBAR ── */}
                    <div className="flex flex-wrap gap-3 mb-4 items-center">
                        {/* Search */}
                        <div className="relative flex-1 min-w-[220px]">
                            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input type="text" placeholder="Search control no, dept, prepared by..."
                                value={search} onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
                                className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                        </div>

                        {/* Status filter */}
                        <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setCurrentPage(1); }}
                            className="border border-gray-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer min-w-[140px]">
                            <option value="">All Statuses</option>
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="disapproved">Disapproved</option>
                        </select>

                        {/* Dept filter */}
                        <select value={filterDept} onChange={e => { setFilterDept(e.target.value); setCurrentPage(1); }}
                            className="border border-gray-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer min-w-[160px]">
                            <option value="">All Departments</option>
                            {departments.map(d => <option key={d.id} value={d.departmentName}>{d.departmentName}</option>)}
                        </select>

                        {/* Date filter */}
                        <input type="date" value={filterDate} onChange={e => { setFilterDate(e.target.value); setCurrentPage(1); }}
                            className="border border-gray-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" />

                        {/* Clear */}
                        {(search || filterStatus || filterDept || filterDate) && (
                            <button onClick={() => { setSearch(""); setFilterStatus(""); setFilterDept(""); setFilterDate(""); setCurrentPage(1); }}
                                className="flex items-center gap-1.5 px-3 py-2.5 rounded-lg border border-red-300 bg-white text-red-600 text-sm font-semibold hover:bg-red-50 transition-colors cursor-pointer">
                                <X size={14} /> Clear
                            </button>
                        )}

                        {/* Export */}
                        <button onClick={exportToExcel}
                            className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-green-600 text-white text-sm font-bold hover:bg-green-700 transition-colors cursor-pointer shadow-sm">
                            <Download size={15} /> Export Excel
                        </button>

                        {/* Refresh */}
                        <button onClick={fetchRecords}
                            className="flex items-center gap-1.5 px-3 py-2.5 rounded-lg border border-gray-300 bg-white text-gray-600 text-sm font-semibold hover:bg-gray-50 transition-colors cursor-pointer">
                            <RefreshCw size={15} />
                        </button>
                    </div>

                    {/* Record count */}
                    {!loading && (
                        <p className="text-xs text-slate-500 mb-3">
                            {filtered.length} record{filtered.length !== 1 ? "s" : ""} found
                            {(search || filterStatus || filterDept || filterDate) && <span className="text-blue-600 font-semibold"> (filtered)</span>}
                        </p>
                    )}

                    {/* ── TABLE ── */}
                    {loading ? (
                        <div className="flex items-center justify-center py-16 text-slate-400 gap-3">
                            <RefreshCw size={20} className="animate-spin" />
                            <span className="text-sm font-medium">Loading records...</span>
                        </div>
                    ) : paginated.length === 0 ? (
                        <div className="text-center py-16 text-slate-400 text-sm">
                            {records.length > 0 ? "No records match your search." : "No disposal records found."}
                        </div>
                    ) : (
                        <div className="overflow-x-auto rounded-xl border border-gray-200">
                            <table className="w-full text-sm border-collapse">
                                <thead>
                                    <tr className="bg-blue-800 text-white">
                                        {["#", "Control No", "Department", "Date", "Prepared By", "Item Name", "Qty", "Unit", "Status", "Action"].map((h, i, arr) => (
                                            <th key={i} className={`px-3 py-3 text-left text-xs font-bold uppercase tracking-wide whitespace-nowrap ${h === "Action" ? "text-center" : ""} ${i === 0 ? "rounded-tl-xl" : ""} ${i === arr.length - 1 ? "rounded-tr-xl" : ""}`}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginated.map((row, idx) => (
                                        <tr key={f.id(row) || idx}
                                            className={`border-b border-gray-100 transition-colors hover:bg-blue-50 ${idx % 2 === 0 ? "bg-white" : "bg-slate-50"}`}>
                                            <td className="px-3 py-3 text-center text-slate-400 text-xs w-10">{(currentPage - 1) * PAGE_SIZE + idx + 1}</td>
                                            <td className="px-3 py-3 font-bold text-blue-700 whitespace-nowrap">{f.controlNo(row) || "—"}</td>
                                            <td className="px-3 py-3 text-slate-700">{f.department(row) || "—"}</td>
                                            <td className="px-3 py-3 text-slate-600 whitespace-nowrap">{f.date(row) ? new Date(f.date(row)).toLocaleDateString("en-US") : "—"}</td>
                                            <td className="px-3 py-3 text-slate-700">{f.preparedBy(row) || "—"}</td>
                                            <td className="px-3 py-3 text-slate-700 max-w-[160px] truncate" title={f.itemName(row)}>{f.itemName(row) || "—"}</td>
                                            <td className="px-3 py-3 text-center text-slate-600">{f.quantity(row) || "—"}</td>
                                            <td className="px-3 py-3 text-center text-slate-600">{f.unit(row) || "—"}</td>
                                            <td className="px-3 py-3"><StatusBadge row={row} /></td>
                                            {/* VIEW ONLY — no approve/disapprove in table */}
                                            <td className="px-3 py-3 text-center">
                                                <button onClick={() => setSelectedRow(row)}
                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-600 text-xs font-semibold hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition-all cursor-pointer">
                                                    <Eye size={13} /> View
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* ── PAGINATION ── */}
                    {!loading && filtered.length > PAGE_SIZE && (
                        <div className="flex justify-between items-center mt-5">
                            <span className="text-xs text-slate-500">
                                Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filtered.length)} of {filtered.length}
                            </span>
                            <div className="flex gap-1.5 items-center">
                                <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className={paginBtn(currentPage === 1)}>«</button>
                                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className={paginBtn(currentPage === 1)}>‹</button>
                                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                    let page;
                                    if (totalPages <= 5) page = i + 1;
                                    else if (currentPage <= 3) page = i + 1;
                                    else if (currentPage >= totalPages - 2) page = totalPages - 4 + i;
                                    else page = currentPage - 2 + i;
                                    return (
                                        <button key={page} onClick={() => setCurrentPage(page)}
                                            className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition-all cursor-pointer ${page === currentPage ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"}`}>
                                            {page}
                                        </button>
                                    );
                                })}
                                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className={paginBtn(currentPage === totalPages)}>›</button>
                                <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className={paginBtn(currentPage === totalPages)}>»</button>
                            </div>
                        </div>
                    )}
                </div>

                {/* PAGE FOOTER */}
                <div className="px-8 py-3 border-t border-gray-100 bg-slate-50 flex justify-between items-center">
                    <p className="text-[10px] text-slate-400 italic">This document is a property of Mega Soft. Unauthorized copying, reproduction, distribution, or alteration is strictly prohibited.</p>
                    <span className="text-xs text-slate-400">{new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>
                </div>
            </div>

            {/* ══════════════ DETAIL MODAL ══════════════ */}
{/* ══════════════ DETAIL MODAL ══════════════ */}
{selectedRow && (() => {
    const status    = getStatus(selectedRow);
    const isLoading = actionLoading === f.id(selectedRow);
    const cfg       = STATUS_CONFIG[status];

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={e => { if (e.target === e.currentTarget) setSelectedRow(null); }}>

            <div className="bg-white rounded-3xl w-full max-w-6xl max-h-[95vh] flex flex-col overflow-hidden"
                style={{ boxShadow: "0 32px 80px rgba(0,0,0,0.40), 0 8px 24px rgba(0,0,0,0.20)" }}>

                {/* ── HEADER ── */}
                <div className="relative flex-shrink-0 overflow-hidden"
                    style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 50%, #1d4ed8 100%)" }}>

                    {/* Decorative circles */}
                    <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full opacity-10"
                        style={{ background: "radial-gradient(circle, #60a5fa, transparent)" }} />
                    <div className="absolute -bottom-6 left-20 w-24 h-24 rounded-full opacity-10"
                        style={{ background: "radial-gradient(circle, #93c5fd, transparent)" }} />

                    <div className="relative px-7 py-5 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            {/* Icon box */}
                            <div className="bg-white/10 border border-white/20 p-2.5 rounded-2xl">
                                <ClipboardList size={22} className="text-white" />
                            </div>
                            <div>
                                <p className="text-blue-300 text-[10px] font-bold uppercase tracking-[0.2em] mb-0.5">
                                    Mega Soft · Spareparts Department
                                </p>
                                <h2 className="text-white font-extrabold text-lg leading-none tracking-tight">
                                    Item Disposal Form
                                </h2>
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            {/* Control No pill */}
                            <div className="text-right bg-white/10 border border-white/20 rounded-xl px-4 py-2">
                                <p className="text-blue-300 text-[9px] font-bold uppercase tracking-widest">Control No.</p>
                                <p className="text-white font-extrabold text-sm tracking-wider leading-tight mt-0.5">
                                    {f.controlNo(selectedRow) || "—"}
                                </p>
                            </div>
                            {/* Status badge */}
                            <StatusBadge row={selectedRow} />
                            {/* Close */}
                            <button onClick={() => setSelectedRow(null)}
                                className="bg-white/10 hover:bg-white/20 border border-white/20 text-white rounded-xl w-9 h-9 flex items-center justify-center transition-all cursor-pointer">
                                <X size={16} />
                            </button>
                        </div>
                    </div>

                    {/* Meta info strip */}
                    <div className="relative border-t border-white/10 px-7 py-2.5 flex items-center gap-1 flex-wrap"
                        style={{ background: "rgba(0,0,0,0.25)" }}>
                        {[
                            { icon: CalendarDays, label: "Date", value: f.date(selectedRow) ? new Date(f.date(selectedRow)).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "—" },
                            { icon: Building2,    label: "Department", value: f.department(selectedRow) || "—" },
                            { icon: Users,        label: "Prepared By", value: f.preparedBy(selectedRow) || "—" },
                        ].map((item, i) => {
                            const Icon = item.icon;
                            return (
                                <React.Fragment key={i}>
                                    {i > 0 && <span className="w-px h-4 bg-white/20 mx-3" />}
                                    <span className="flex items-center gap-2 text-xs">
                                        <Icon size={12} className="text-blue-400" />
                                        <span className="text-white/50">{item.label}:</span>
                                        <span className="text-white font-semibold">{item.value}</span>
                                    </span>
                                </React.Fragment>
                            );
                        })}
                    </div>
                </div>

                {/* ── DOCUMENT BODY ── */}
                <div className="flex-1 overflow-y-auto" style={{ background: "#e8edf5" }}>
                    <div className="p-6">
                        {/* Paper */}
                        <div id="disposal-print-area" className="bg-white rounded-2xl overflow-hidden"
                            style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)" }}>
                            <DisposalDocument row={selectedRow} />
                        </div>

                        {/* Status banner */}
                        {status !== "pending" && (
                            <div className={`mt-4 p-4 rounded-2xl border-2 flex items-center gap-3 ${cfg.bg} ${cfg.border}`}>
                                {React.createElement(cfg.icon, { size: 20, className: cfg.iconColor })}
                                <div>
                                    <p className={`font-extrabold text-sm ${cfg.color}`}>
                                        This form has been {cfg.label}
                                    </p>
                                    <p className="text-xs text-slate-500 mt-0.5">
                                        Control No: <span className="font-bold">{f.controlNo(selectedRow)}</span>
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* ── FOOTER ── */}
                <div className="flex-shrink-0 px-7 py-4 bg-white border-t border-gray-100 flex items-center justify-between gap-3 flex-wrap rounded-b-3xl"
                    style={{ boxShadow: "0 -4px 16px rgba(0,0,0,0.06)" }}>

                    <div className="flex items-center gap-2 flex-wrap">
                        {status !== "approved" && (
                            <button onClick={() => handleAction(selectedRow, "approve")} disabled={isLoading}
                                className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-bold transition-all cursor-pointer disabled:opacity-50 border-0 shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0"
                                style={{ background: "linear-gradient(135deg, #15803d, #16a34a)" }}>
                                <CheckCircle size={15} />
                                {isLoading ? "Processing..." : "Approve"}
                            </button>
                        )}
                        {status !== "disapproved" && (
                            <button onClick={() => handleAction(selectedRow, "disapprove")} disabled={isLoading}
                                className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-bold transition-all cursor-pointer disabled:opacity-50 border-0 shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0"
                                style={{ background: "linear-gradient(135deg, #b91c1c, #dc2626)" }}>
                                <XCircle size={15} />
                                {isLoading ? "Processing..." : "Disapprove"}
                            </button>
                        )}
                        {status !== "pending" && (
                            <button onClick={() => handleResetStatus(selectedRow)} disabled={isLoading}
                                className="flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-amber-200 bg-amber-50 hover:bg-amber-100 text-amber-800 text-sm font-bold transition-all cursor-pointer disabled:opacity-50 hover:-translate-y-0.5 active:translate-y-0">
                                <RotateCcw size={14} />
                                {isLoading ? "Processing..." : "Reset to Pending"}
                            </button>
                        )}
                        <button onClick={() => window.print()}
                            className="flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-blue-100 bg-blue-50 hover:bg-blue-100 text-blue-700 text-sm font-bold transition-all cursor-pointer hover:-translate-y-0.5 active:translate-y-0">
                            <Printer size={14} />
                            Print / PDF
                        </button>
                    </div>

                    <button onClick={() => setSelectedRow(null)}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-600 text-sm font-bold transition-all cursor-pointer hover:-translate-y-0.5 active:translate-y-0">
                        <X size={14} /> Close
                    </button>
                </div>

            </div>
        </div>
    );
})()}
        </div>
    );
};

export default DisposalApproval;